import React, { useContext } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import { Box, Button, InputLabel, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { required } from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { FormSelectField } from "UI/FormSelectField";
import { deviceManufacturers } from "models/constants";
import { useCloseAndReset } from "hooks/useCloseAndReset";

const awsRegions = {
  "us-east-1": "us-east-1 (N. Virginia)",
  "us-east-2": "us-east-2 (Ohio)",
  "us-west-1": "us-west-1 (N. California)",
  "us-west-2": "us-west-2 (Oregon)",
};

const manufacturers = Object.keys(deviceManufacturers);
const isOnlyOneManufacturer = manufacturers?.length === 1;

const defaultFormData = {
  deviceId: "",
  awsRegion: awsRegions["us-west-2"],
  manufacturer: isOnlyOneManufacturer ? manufacturers[0] : "",
  step: 0,
};

export const ClaimNewRealButtonDialog = ({
  open,
  handleClose,
  refreshList,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning } = useContext(GlobalContext);
  const spinner = useAppSelector((state) => state.spinner);

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const step = watch("step");
  const manufacturer = watch("manufacturer");

  const showAddedWarning = () =>
    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {manufacturer} Device added successfully
          </Typography>
        </Stack>
      ),
      content: (
        <Box mt={4} mb={4}>
          You can send this device {getValues()?.deviceId} to your customer.
        </Box>
      ),
    });

  const forceCloseAndReset = () => {
    handleClose();
    reset(defaultFormData);
  };

  const onSubmit = createApiCall(async () => {
    const body = getValues();
    body.deviceId = body.deviceId.trim();

    const result = await API.put("restApi", "/buttons", {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result?.message ?? result.error));
      return;
    }

    if (result?.actionResult === "ADDITION_COMPLETE") {
      showAddedWarning();
      refreshList();
      forceCloseAndReset();
    } else {
      dispatch(showError("Something went wrong"));
    }
  });

  const handleContinue = createApiCall(async () => {
    const body = getValues();
    body.action = "CONFIRM_BUTTON_PRESS";
    body.deviceId = body.deviceId.trim();

    const result = await API.put("restApi", "/buttons", {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showAddedWarning();
    refreshList();
    forceCloseAndReset();
  });

  const closeAndReset = useCloseAndReset(forceCloseAndReset, isDirty);

  if (step === 0) {
    return (
      <DialogWindow
        open={open}
        reducedSize
        withCloseButton
        handleClose={closeAndReset}
        title={"Add Button Device"}
        content={
          <Box mt={2} mb={2}>
            Select manufacturer of the device you want to add:
            <Stack alignItems={"flex-start"} flex={1}>
              <FormSelectField
                id="manufacturer"
                name="manufacturer"
                control={control}
                options={deviceManufacturers}
                rules={{ required }}
              />
            </Stack>
          </Box>
        }
        actions={
          <Button
            variant="contained"
            onClick={() => setValue("step", 1)}
            disabled={!watch("manufacturer")}
          >
            Continue
          </Button>
        }
      />
    );
  }

  if (step === 1 && manufacturer === deviceManufacturers.SORACOM) {
    return (
      <DialogWindow
        open={open}
        reducedSize
        withCloseButton
        handleClose={closeAndReset}
        title={"Add New SORACOM Button Device"}
        content={
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack mt={2} mb={2} gap={3}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="deviceId">Enter Device SIM ID</InputLabel>
                <FormTextField
                  id="deviceId"
                  name="deviceId"
                  control={control}
                  rules={{ required }}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
            </Stack>
          </form>
        }
        actions={
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={spinner}
          >
            Add Button Device
          </Button>
        }
      />
    );
  }

  return (
    <DialogWindow
      open={open}
      reducedSize
      withCloseButton
      handleClose={closeAndReset}
      title={"Unknown Device"}
      content={<Box>This type of device is not supported yet.</Box>}
      actions={
        <Button variant="contained" onClick={closeAndReset}>
          Got it
        </Button>
      }
    />
  );
};
